import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Heart,
  Sparkles,
  HeartHandshake,
  FileSignature,
  Church,
  BadgeCheck,
  Globe,
  MapPin,
  Mail,
  Phone,
} from "lucide-react";
import {
  PageHero,
  GoldButton,
  SectionEyebrow,
  FadedBackdrop,
} from "@/components/ui-bits";
import { IMG } from "@/constants/images";

// SEO structured data — WeddingService as Schema.org Service on OGO's LocalBusiness
const WEDDING_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Wedding Officiant & Ministerial Services",
  provider: { "@id": "https://ogonotary.com/#business" },
  name: "Wedding Officiant & Ministerial Services — Brevard County, Florida",
  description:
    "Legally ordained minister and authorized Florida Wedding Officiant Dr. Gregory A. Byers provides custom wedding ceremonies, elopements, micro-weddings, vow renewals, and sign-and-go civil services across Brevard County — with post-ceremony notarial support for name changes, Social Security updates, passport updates, and international apostille processing.",
  areaServed: [
    { "@type": "City", "name": "Cocoa Beach, FL" },
    { "@type": "City", "name": "Indialantic, FL" },
    { "@type": "City", "name": "Melbourne, FL" },
    { "@type": "City", "name": "Palm Bay, FL" },
    { "@type": "AdministrativeArea", "name": "Brevard County, Florida" },
  ],
  offers: [
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Custom Wedding Ceremonies" } },
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Elopements & Micro-Weddings" } },
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Vow Renewals" } },
    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Sign-and-Go Civil Marriage Ceremonies" } },
  ],
};

const OFFERINGS = [
  {
    icon: Heart,
    title: "Custom Wedding Ceremonies",
    body:
      "Full-service officiating tailored completely to your style, personal values, and faith tradition. Includes pre-ceremony consultations to craft personalized vows.",
  },
  {
    icon: Sparkles,
    title: "Elopements & Micro-Weddings",
    body:
      "Elegant, intimate, and swift ceremonies for couples looking to celebrate their commitment in a smaller, low-stress environment.",
  },
  {
    icon: HeartHandshake,
    title: "Vow Renewals",
    body:
      "Re-anchor your foundation. Celebrate the milestones of your enduring marriage by reaffirming the vows that built your family legacy.",
  },
  {
    icon: FileSignature,
    title: "Sign-and-Go Civil Services",
    body:
      "Need to handle the formalities of your civil ceremony securely and swiftly? We provide efficient marriage license verification and notarial execution at a moment's notice.",
  },
];

const TRANSITION_SUPPORT = [
  "Official Certified Marriage Certificate filings",
  "Name change documentation & notarization support",
  "Social Security Office records updates",
  "Driver's License, Passport, and state identity updates",
  "International Apostille processing for cross-border marriages",
];

const SERVICE_AREAS = [
  "Cocoa Beach",
  "Indialantic",
  "Melbourne",
  "Palm Bay",
  "Space Coast",
];

export default function WeddingServicesPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(WEDDING_JSONLD) }}
      />

      <PageHero
        eyebrow="Wedding Officiant & Ministerial Services"
        title="Grounded in Faith, Built on Experience, Celebrating Love."
        lead="Uniting lives across Brevard County & beyond. Legally ordained minister and authorized Florida Wedding Officiant Dr. Gregory A. Byers officiates formal, personalized, and deeply meaningful ceremonies tailored to your unique love story — with the administrative expertise to guide you through everything that comes after 'I do.'"
        backdrop={IMG.weddingHero}
        backdropOpacity={0.7}
        bright
      />

      {/* INTRO — narrative block */}
      <section className="relative overflow-hidden py-24 sm:py-32 border-b border-white/10">
        <FadedBackdrop src={IMG.weddingRings} opacity={0.55} bright />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4 flex lg:justify-center">
              <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
                <Heart className="w-7 h-7" />
              </div>
            </div>
            <div className="lg:col-span-8 space-y-6">
              <SectionEyebrow>A Sacred Milestone. A Lifetime Legacy.</SectionEyebrow>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                A wedding is more than a legal contract — it is a sacred
                milestone, a celebration of commitment, and the foundation of
                a lifetime legacy. As a legally ordained minister and
                authorized Florida Wedding Officiant, Dr. Gregory A. Byers
                provides formal, personalized, and deeply meaningful wedding
                officiating services tailored to your unique love story.
              </p>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                Whether you are planning an intimate beach ceremony along the
                Brevard coast, a traditional church wedding, a formal
                corporate-scale venue celebration, or a swift civil ceremony,
                we ensure your vows are spoken with dignity, warmth, and legal
                perfection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CEREMONIAL OFFERINGS */}
      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <div className="max-w-2xl">
              <SectionEyebrow className="mb-4">Our Ceremonial Offerings</SectionEyebrow>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
                Four ways to celebrate — tailored to your love story.
              </h2>
            </div>
            <p className="text-slate-400 max-w-md text-sm leading-relaxed">
              Every ceremony is planned with pre-ceremony consultation, faith
              tradition sensitivity, and legal precision so nothing between
              you and your happily-ever-after is left to chance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {OFFERINGS.map((o, i) => (
              <OfferingCard key={i} offering={o} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* OGO ADVANTAGE — POST-CEREMONY TRANSITION SUPPORT */}
      <section className="relative overflow-hidden py-24 sm:py-32 border-b border-white/10 bg-[#0F1B2E]">
        <FadedBackdrop src={IMG.weddingBeach} opacity={0.5} bright />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionEyebrow className="mb-4">The OGO Advantage</SectionEyebrow>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
                Post-ceremony transition support — because &quot;I do&quot; is
                just the beginning.
              </h2>
              <p className="mt-6 text-slate-300 leading-relaxed">
                When you marry, your legal name and records change. Unlike
                standard officiants who leave you at the altar to figure out
                the paperwork on your own, OGO Notary leverages our deep
                administrative expertise to guide you through your entire
                post-wedding administrative transition.
              </p>
              <p className="mt-4 text-slate-300 leading-relaxed">
                We provide authorized notarization, administrative process
                guidance, and step-by-step document support to help you
                seamlessly navigate your post-wedding transition:
              </p>
            </div>

            <div className="lg:col-span-7">
              <ul className="space-y-4">
                {TRANSITION_SUPPORT.map((item, i) => (
                  <li
                    key={i}
                    data-testid={`wedding-transition-${i}`}
                    className="flex items-start gap-4 border border-white/10 bg-[#1A2B45] p-5 hover:border-[#D4AF37]/40 transition-colors"
                  >
                    <div className="shrink-0 w-9 h-9 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37]">
                      <BadgeCheck className="w-5 h-5" />
                    </div>
                    <div className="text-slate-200 text-base leading-relaxed pt-1">
                      {item}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREA + CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <div className="mx-auto w-16 h-16 rounded-full border-2 border-[#D4AF37] flex items-center justify-center text-[#D4AF37] mb-8">
            <Church className="w-7 h-7" />
          </div>
          <SectionEyebrow className="mb-4">Let&apos;s Plan Your Milestone Moment</SectionEyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
            Your ceremony should reflect your values, your journey, and your
            faith.
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl mx-auto">
            Let&apos;s collaborate to build an unforgettable experience —
            spoken with dignity, warmth, and legal perfection.
          </p>

          {/* Service area chips */}
          <div className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-4 flex items-center justify-center gap-2">
              <MapPin className="w-3 h-3" /> Serving Brevard County &amp; Surrounding Space Coast
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {SERVICE_AREAS.map((city) => (
                <span
                  key={city}
                  data-testid={`wedding-area-${city.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-[11px] uppercase tracking-[0.22em] font-semibold border border-[#D4AF37]/40 text-[#D4AF37] px-4 py-2"
                >
                  {city}
                </span>
              ))}
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
            <GoldButton
              as={Link}
              to="/contact?service=Wedding+Officiant"
              data-testid="wedding-cta-consultation"
              className="justify-center"
            >
              Schedule a Wedding Consultation <ArrowUpRight className="w-4 h-4" />
            </GoldButton>
            <a
              href="tel:+18555054722"
              data-testid="wedding-cta-call"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B1120] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Phone className="w-4 h-4" /> Call Directly: (855) 505-4722
            </a>
          </div>
          <div className="mt-6">
            <a
              href="mailto:Greg@ogonotary.com?subject=Wedding%20Officiant%20Inquiry"
              data-testid="wedding-cta-email"
              className="inline-flex items-center gap-2 text-slate-300 hover:text-[#D4AF37] text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Mail className="w-4 h-4" /> Or email Greg@ogonotary.com
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function OfferingCard({ offering, index }) {
  const Icon = offering.icon;
  return (
    <div
      data-testid={`wedding-offering-${index}`}
      className="group border border-white/10 bg-[#1A2B45] p-8 hover:border-[#D4AF37]/50 transition-colors relative overflow-hidden"
    >
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#D4AF37]/[0.04] blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="w-12 h-12 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] mb-6">
          <Icon className="w-6 h-6" />
        </div>
        <h3 className="font-serif-display text-2xl text-white mb-4 leading-tight">
          {offering.title}
        </h3>
        <p className="text-slate-300 text-sm leading-relaxed">
          {offering.body}
        </p>
      </div>
    </div>
  );
}
